import { useState, useCallback } from "react";
import {
  isDrillSmartGrouping,
  normalizeWorkToModalData,
  type DrillSmartModalData,
} from "utils/functions/drillSmart";

export interface UseDrillSmartReturn {
  // Current modal open state and data (data is null when closed or when data is empty string for Home compat)
  modalState: { isOpen: boolean; data: DrillSmartModalData | null | "" };
  // Open the DrillSmart modal with the given work item (must be a DrillSmart grouping)
  openModal: (work: any) => void;
  // Close the DrillSmart modal
  closeModal: () => void;
  // Call after refresh: re-sync modal data from the provided finder (e.g. from new API response). Finder receives current groupName
  reSyncModalData: (findUpdatedData: (groupName: string | null) => DrillSmartModalData | null) => void;
  // Whether the given work is a DrillSmart grouping (opens modal instead of launching)
  isDrillSmartGrouping: (work: any) => boolean;
  // Normalize work to modal data shape (for opening modal)
  normalizeWorkToModalData: (work: any) => DrillSmartModalData;
}


export function useDrillSmart(): UseDrillSmartReturn {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    data: DrillSmartModalData | null | "";
  }>({ isOpen: false, data: "" });

  const openModal = useCallback((work: any) => {
    if (!isDrillSmartGrouping(work)) return;
    setModalState({
      isOpen: true,
      data: normalizeWorkToModalData(work),
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const reSyncModalData = useCallback(
    (findUpdatedData: (groupName: string | null) => DrillSmartModalData | null) => {
      setModalState((prev) => {
        if (!prev.isOpen || !prev.data) return prev;
        const groupName =
          typeof prev.data === "object" && prev.data && "groupName" in prev.data
            ? (prev.data as DrillSmartModalData).groupName ?? null
            : null;
        const updated = findUpdatedData(groupName);
        if (!updated) return prev;
        return { ...prev, data: updated };
      });
    },
    []
  );

  return {
    modalState,
    openModal,
    closeModal,
    reSyncModalData,
    isDrillSmartGrouping,
    normalizeWorkToModalData,
  };
}
